import React, { useState } from 'react';

import { gmailInviteService, GmailInvitePreview, InviteProject, InviteTarget } from '../services/gmailInviteService';

import { InviteEmailCard } from './InviteEmailCard';

const RECENT = 3;

const edited = (iso: string | null): string => {
  if (!iso) return 'Your project';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);

  if (days <= 0) return 'Edited today';
  if (days === 1) return 'Edited yesterday';

  return days < 30 ? `Edited ${days} days ago` : `Edited ${new Date(iso).toLocaleDateString()}`;
};

export const seatsNote = (project: InviteProject | undefined): string | null => {
  if (!project || project.seats_left === null) return null;
  if (project.seats_left === 0) {
    return 'Your plan has no room for more people on this project. Upgrade, or invite them to 8080.AI instead.';
  }

  return `Your plan has room for ${project.seats_left} more ${project.seats_left === 1 ? 'person' : 'people'} here.`;
};

interface ProjectCardsProps {
  projects: InviteProject[];
  target: InviteTarget;
  onPick: (projectId: string | null) => void;
}

/** The three projects worked on last, any other project by search, or 8080.AI with no project. */
const ProjectCards: React.FC<ProjectCardsProps> = ({ projects, target, onPick }) => {
  const [query, setQuery] = useState('');
  const chosen = projects.find((project) => project.id === target.projectId);
  const recent = projects.slice(0, RECENT);
  const shown = chosen && !recent.includes(chosen) ? [...recent, chosen] : recent;
  const matches = query ? projects.filter((project) => project.title.toLowerCase().includes(query.toLowerCase())) : [];

  return (
    <div className="invite-where">
      <div className="invite-where__grid">
        {shown.map((project) => (
          <button aria-pressed={project.id === target.projectId} className={`am-card${project.id === target.projectId ? ' is-selected' : ''}`}
            key={project.id} onClick={() => onPick(project.id)} type="button">
            <span className="am-card-title">{project.title}</span>
            <span className="am-card-meta">{edited(project.updated_at)}</span>
          </button>
        ))}
        <button aria-pressed={target.projectId === null} className={`am-card${target.projectId === null ? ' is-selected' : ''}`}
          onClick={() => onPick(null)} type="button">
          <span className="am-card-title">Just 8080.AI</span>
          <span className="am-card-meta">A new account, no project</span>
        </button>
      </div>
      {projects.length > RECENT && (
        <div className="invite-where__search">
          <input aria-label="Find another project" className="am-input" onChange={(event) => setQuery(event.target.value)}
            placeholder={`Find another of your ${projects.length} projects`} type="search" value={query} />
          {matches.length > 0 && (
            <ul className="invite-where__matches">
              {matches.slice(0, 6).map((project) => (
                <li key={project.id}>
                  <button className="am-row-button" onClick={() => { onPick(project.id); setQuery(''); }} type="button">
                    {project.title}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

interface AccessChoiceProps {
  target: InviteTarget;
  onChange: (target: InviteTarget) => void;
}

const AccessChoice: React.FC<AccessChoiceProps> = ({ target, onChange }) => (
  <div className="invite-where__access">
    <span className="am-note">They can</span>
    <div aria-label="Access" className="am-segmented" role="group">
      {([['reader', 'View'], ['writer', 'Edit']] as const).map(([role, label]) => (
        <button aria-pressed={target.role === role} className={`am-segment${target.role === role ? ' is-active' : ''}`}
          key={role} onClick={() => onChange({ ...target, role })} type="button">{label}</button>
      ))}
    </div>
  </div>
);

/** The sample email: a link in the button row opens it, and it shows on its own line above that row. */
const useSampleEmail = (target: InviteTarget) => {
  const [preview, setPreview] = useState<GmailInvitePreview | null>(null);
  const [open, setOpen] = useState(false);
  const toggle = () => {
    setOpen((value) => !value);
    void gmailInviteService.samplePreview(target).then(setPreview).catch(() => undefined);
  };

  return { open, preview, toggle };
};

interface InviteWhereStepProps {
  projects: InviteProject[] | null;
  target: InviteTarget;
  onChange: (target: InviteTarget) => void;
  /** "Choose people from Gmail", or back to the list when one is already waiting. */
  next: { label: string; busy: boolean; onClick: () => void };
}

/** Step 1: where people are invited to, and with what access. */
export const InviteWhereStep: React.FC<InviteWhereStepProps> = ({ projects, target, onChange, next }) => {
  const chosen = projects?.find((project) => project.id === target.projectId);
  const full = chosen?.seats_left === 0;
  const note = seatsNote(chosen);
  const sample = useSampleEmail(target);

  return (
    <section className="invite-step">
      <div className="am-header">
        <h3 className="am-title">Invite people to work with you</h3>
        <p className="am-lede">
          Pick a project and what they can do, then choose people from your Google contacts. Nothing is sent until
          you press Send.
        </p>
      </div>
      {projects === null
        ? <div aria-busy="true" className="invite-skeleton invite-skeleton--cards">Loading your projects…</div>
        : (
          <ProjectCards
            onPick={(projectId) => onChange({ ...target, projectId })}
            projects={projects}
            target={target}
          />
        )}
      {target.projectId && <AccessChoice onChange={onChange} target={target} />}
      {note && <p className={`am-note${full ? ' am-note--warning' : ''}`}>{note}</p>}
      {sample.open && <InviteEmailCard preview={sample.preview} />}
      <p className="am-note">
        Google will ask to let 8080.AI see your contacts&apos; names and email addresses and send the invitations you
        pick from your Gmail. We never read your email.{' '}
        <a href="/privacy" rel="noopener noreferrer" target="_blank">Privacy Policy</a>
      </p>
      <div className="am-actions am-actions--split">
        <button aria-expanded={sample.open} className="am-link" onClick={sample.toggle} type="button">
          {sample.open ? 'Hide the email' : 'Preview the email'}
        </button>
        <button className="am-cta" disabled={next.busy || full || projects === null} onClick={next.onClick} type="button">
          {next.label}
        </button>
      </div>
    </section>
  );
};
